'use client' // Ensure this is present if using hooks

import { useEffect, useState } from 'react'

// Next Imports
import { useParams } from 'next/navigation'

import { useTheme } from '@mui/material/styles'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports
import type { getDictionary } from '@/utils/getDictionary'

import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'

// Component Imports
import { Menu, SubMenu, MenuItem, MenuSection } from '@menu/vertical-menu'

// import { GenerateVerticalMenu } from '@components/GenerateMenu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'

// Styled Component Imports
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuItemStyles from '@core/styles/vertical/menuItemStyles'

import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'

// Data Imports
import { getFeaturesConfigData } from '@/app/server/actions'

// Type Imports
import type { FeaturesConfigType } from '@/types/apps/featuresConfigType'

type RenderExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

type Props = {
  dictionary: Awaited<ReturnType<typeof getDictionary>>
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='ri-arrow-right-s-line' />
  </StyledVerticalNavExpandIcon>
)

const VerticalMenu = ({ dictionary, scrollMenu }: Props) => {
  const [data, setData] = useState<FeaturesConfigType[]>([])

  useEffect(() => {
    const fetchData = async () => {
      const result = await getFeaturesConfigData() // Fetch data here

      setData(result)
    }

    fetchData()
  }, [])

  // Hooks
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const params = useParams()

  // Vars
  const { isBreakpointReached, transitionDuration } = verticalNavOptions
  const { lang: locale } = params

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  return (
    // eslint-disable-next-line lines-around-comment
    /* Custom scrollbar instead of browser scroll, remove if you want browser scroll only */
    <ScrollWrapper
      {...(isBreakpointReached
        ? {
            className: 'bs-full overflow-y-auto overflow-x-hidden',
            onScroll: container => scrollMenu(container, false)
          }
        : {
            options: { wheelPropagation: false, suppressScrollX: true },
            onScrollY: container => scrollMenu(container, true)
          })}
    >
      {/* Incase you also want to scroll NavHeader to scroll with Vertical Menu, remove NavHeader from above and paste it below this comment */}
      {/* Vertical Menu */}
      <Menu
        popoutMenuOffset={{ mainAxis: 10 }}
        menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
        renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
        renderExpandedMenuItemIcon={{ icon: <i className='ri-circle-line' /> }}
        menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
        className='pb-16'
      >
        <MenuItem href={`/${locale}/home`} icon={<i className='ri-home-3-line'></i>}>
          <span>Home</span>
        </MenuItem>
        {data.map((feature, index) => {
          if (!feature.enabled) return null // Skip if not enabled

          switch (feature.type) {
            case 'Vectax':
              return (
                <SubMenu
                  key={feature.type + index}
                  label={dictionary['navigation'].vectax}
                  icon={<i className='ri-instance-line' />}
                >
                  {feature.overview && (
                    <MenuItem href={`/${locale}/vectax/overview`}>{dictionary['navigation'].overview}</MenuItem>
                  )}
                  {feature.playground && (
                    <MenuItem href={`/${locale}/vectax/playground`}>{dictionary['navigation'].playground}</MenuItem>
                  )}
                  {feature.myplan && (
                    <MenuItem href={`/${locale}/vectax/myPlan`}>{dictionary['navigation'].myplan}</MenuItem>
                  )}
                  <MenuItem href={`/${locale}/vectax/documentation`}>{dictionary['navigation'].documentation}</MenuItem>
                </SubMenu>
              )

            case 'Agent IQ':
              return (
                <SubMenu key={feature.type + index} label={'Agent IQ'} icon={<i className='ri-shield-star-line' />}>
                  {feature.overview && (
                    <MenuItem href={`/${locale}/agentIq/overview`}>{dictionary['navigation'].overview}</MenuItem>
                  )}
                  {feature.dashboard && (
                    <MenuItem href={`/${locale}/agentIq/dashboard`}>{dictionary['navigation'].dashboard}</MenuItem>
                  )}
                  {feature.playground && (
                    <MenuItem href={`/${locale}/agentIq/playground`}>{dictionary['navigation'].playground}</MenuItem>
                  )}
                  {feature.policyManager && (
                    <MenuItem href={`/${locale}/agentIq/policyManager`}>
                      {dictionary['navigation'].policyManager}
                    </MenuItem>
                  )}
                  <MenuItem href={`/${locale}/agentIq/traces`}>Traces</MenuItem>
                  {feature.myplan && (
                    <MenuItem href={`/${locale}/agentIq/myPlan`}>{dictionary['navigation'].myplan}</MenuItem>
                  )}
                  <MenuItem href={`/${locale}/agentIq/documentation`}>
                    {dictionary['navigation'].documentation}
                  </MenuItem>
                </SubMenu>
              )
            case 'Discover':
              return (
                <SubMenu
                  key={feature.type + index}
                  label={dictionary['navigation'].discover}
                  icon={<i className='ri-search-eye-line' />}
                >
                  {feature.overview && (
                    <MenuItem href={`/${locale}/discover/overview`}>{dictionary['navigation'].overview}</MenuItem>
                  )}
                  {feature.dashboard && (
                    <MenuItem href={`/${locale}/discover/dashboard`}>{dictionary['navigation'].dashboard}</MenuItem>
                  )}
                  {feature.playground && (
                    <MenuItem href={`/${locale}/discover/playground`}>{dictionary['navigation'].playground}</MenuItem>
                  )}
                  {feature.applications && (
                    <MenuItem href={`/${locale}/discover/applications`}>
                      {dictionary['navigation'].applications}
                    </MenuItem>
                  )}
                  {feature.attackPaths && (
                    <MenuItem href={`/${locale}/discover/attack-paths`}>
                      {dictionary['navigation'].attackPaths}
                    </MenuItem>
                  )}
                  {feature.ciCdPipeline && (
                    <MenuItem href={`/${locale}/discover/ci-cd-pipeline`}>
                      {dictionary['navigation'].CiCdPipeline}
                    </MenuItem>
                  )}
                  {feature.traces && (
                    <MenuItem href={`/${locale}/discover/traces`}>{dictionary['navigation'].traces}</MenuItem>
                  )}
                  <MenuItem href={`/${locale}/discover/documentation`}>
                    {dictionary['navigation'].documentation}
                  </MenuItem>
                </SubMenu>
              )
            case 'Sdk':
              return (
                <MenuSection key={feature.type + index} label='SDK'>
                  {feature.download && (
                    <MenuItem href={`/${locale}/sdk/download`} icon={<i className='ri-import-line' />}>
                      Download
                    </MenuItem>
                  )}
                </MenuSection>
              )

            case 'Configuration':
              return (
                <MenuSection key={feature.type + index} label='Configurations'>
                  {feature.apikeys && (
                    <MenuItem href={`/${locale}/apikeys/keys`} icon={<i className='ri-key-2-line' />}>
                      {dictionary['navigation'].apiKeys}
                    </MenuItem>
                  )}
                  {feature.team && (
                    <MenuItem icon={<i className='ri-team-line' />} href={`/${locale}/teams`}>
                      {dictionary['navigation'].team}
                    </MenuItem>
                  )}
                </MenuSection>
              )

            case 'Account':
              return (
                <MenuSection key={feature.type + index} label='Account'>
                  {feature.settings && (
                    <MenuItem href={`/${locale}/pages/account-settings`} icon={<i className='ri-user-settings-line' />}>
                      {dictionary['navigation'].settings}
                    </MenuItem>
                  )}
                </MenuSection>
              )

            default:
              return null
          }
        })}
      </Menu>
    </ScrollWrapper>
  )
}

export default VerticalMenu
